import { Check } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export interface TimelineStep {
  readonly label: string;
  /** When it happened or is due, or who acts, in caption text. */
  readonly detail?: string;
  readonly state: "done" | "current" | "upcoming";
  /** The current step's ring: `attention` when the reader must act, `progress` when the other side does. */
  readonly tone?: "attention" | "progress";
  /** The state in words for screen readers ("Done", "Next"), since the marks are drawn. */
  readonly stateLabel?: string;
  /** What the step offers under its text, such as a checklist item's action (card 113). */
  readonly content?: ReactNode;
}

/** A step's 18 px mark: done in green with a check, the current one ringed in its tone, the rest grey rings. */
function Mark({ step }: { step: TimelineStep }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative inline-flex size-4.5 shrink-0 items-center justify-center rounded-full",
        step.state === "done" && "bg-tone-done-dot text-on-brand",
        step.state === "current" &&
          (step.tone === "attention"
            ? "border-2 border-tone-attention-dot bg-tone-attention-bg"
            : "border-2 border-tone-progress-dot bg-tone-progress-bg"),
        step.state === "upcoming" && "border-2 border-border-strong bg-surface",
      )}
    >
      {step.state === "done" && <Check size={12} strokeWidth={2.5} />}
    </span>
  );
}

function StepText({ step, grow = false }: { step: TimelineStep; grow?: boolean }) {
  return (
    <div className={cn("flex min-w-0 flex-col", grow && "flex-1")}>
      <span
        className={cn("text-small", step.state === "upcoming" ? "text-ink-muted" : "font-medium text-ink")}
      >
        {step.label}
        {step.stateLabel !== undefined && <span className="sr-only">, {step.stateLabel}</span>}
      </span>
      {step.detail !== undefined && <span className="text-caption text-ink-muted">{step.detail}</span>}
      {step.content !== undefined && <div className="mt-2 flex flex-col gap-2">{step.content}</div>}
    </div>
  );
}

/**
 * A deal's or a case's steps (docs/design/meridian.md): 18 px marks, done in green with a check, the current one
 * ringed, the rest grey rings, joined by a line. `horizontal` lays a few short steps in a row, as onboarding's
 * account, enterprise, and verification (card 113).
 */
export function Timeline({
  steps,
  label,
  orientation = "vertical",
  className,
}: {
  steps: readonly TimelineStep[];
  label?: string;
  orientation?: "vertical" | "horizontal";
  className?: string;
}) {
  if (orientation === "horizontal")
    return (
      <ol aria-label={label} className={cn("flex items-start gap-3", className)}>
        {steps.map((step, index) => (
          <li
            key={`${step.label}|${step.detail ?? ""}`}
            aria-current={step.state === "current" ? "step" : undefined}
            className="flex min-w-0 flex-1 items-start gap-2 last:flex-none"
          >
            <span className="mt-px flex shrink-0">
              <Mark step={step} />
            </span>
            <StepText step={step} />
            {index < steps.length - 1 && (
              <span
                aria-hidden="true"
                className={cn(
                  "mt-2.5 h-px min-w-4 flex-1",
                  step.state === "done" ? "bg-tone-done-dot" : "bg-border",
                )}
              />
            )}
          </li>
        ))}
      </ol>
    );
  return (
    <ol aria-label={label} className={cn("flex flex-col", className)}>
      {steps.map((step, index) => {
        const last = index === steps.length - 1;
        return (
          <li
            key={`${step.label}|${step.detail ?? ""}`}
            aria-current={step.state === "current" ? "step" : undefined}
            className="relative flex gap-3 pb-4 last:pb-0"
          >
            {!last && (
              <span
                aria-hidden="true"
                className={cn(
                  "absolute start-[8.5px] top-5 bottom-0.5 w-px",
                  step.state === "done" ? "bg-tone-done-dot" : "bg-border",
                )}
              />
            )}
            <span className="mt-0.5 flex shrink-0">
              <Mark step={step} />
            </span>
            <StepText step={step} grow />
          </li>
        );
      })}
    </ol>
  );
}
